// composables/useDomaines.ts
import {
  createDomaineValidator,
  patchDomaineValidator,
  updateDomaineValidator,
} from '~/validators/domaine'
import { Domaine } from '~/models/Domaine'
import { useApiMonOrga } from '~/composables/api/useApiMonOrga'
import type { OrganizationKey } from '~~/errors/ConcurrentChangeError'
import type { MergeRecord } from '~~/utils/organization/concurrentPatch'

type RawDomaine = {
    dirLib: string
    domLib: string
    domLibLong?: string
    domDesc?: string
    domOrdre?: number
    agencesId?: string[]
}

function mapToDomaine(raw: RawDomaine): Domaine {
    return new Domaine({
        dirLib: raw.dirLib,
        domLib: raw.domLib,
        domLibLong: raw.domLibLong ?? '',
        domDesc: raw.domDesc ?? '',
        domOrdre: raw.domOrdre ?? 0,
    })
}

const encodeId = (id: string) => encodeURIComponent(id)

export const useDomaines = () => {
    async function storeDomaine(domaineData: Domaine): Promise<Domaine | null> {
        const payload = await createDomaineValidator.validate(domaineData)
        const data = await useApiMonOrga<RawDomaine>('POST', 'domaines', undefined, payload)
        return data ? mapToDomaine(data) : null
    }

    async function fetchDomaineById(
        id: string,
    ): Promise<null | { domaine: Domaine; agencesId: string[] }> {
        const data = await useApiMonOrga<RawDomaine>('GET', `domaines/${encodeId(id)}`)

        if (!data) return null

        const domaine = mapToDomaine(data)

        return {
            domaine,
            agencesId: data.agencesId ?? [],
        }
    }

    async function updateDomaine(id: string, domaineData: Domaine): Promise<Domaine | null> {
        const payload = await updateDomaineValidator.validate(domaineData)
        const data = await useApiMonOrga<RawDomaine>('PUT', `domaines/${encodeId(id)}`, undefined, payload)
        return data ? mapToDomaine(data) : null
    }

    async function patchDomaine(
        id: string,
        partialData: Partial<Domaine>,
    ): Promise<Domaine | null> {
        const payload = await patchDomaineValidator.validate(partialData)
        const data = await useApiMonOrga<RawDomaine>('PATCH', `domaines/${encodeId(id)}`, undefined, payload)
        return data ? mapToDomaine(data) : null
    }

    async function mergeDomaine(
        id: string,
        changes: MergeRecord,
        base: MergeRecord,
    ): Promise<{ domaine: Domaine; currentKey: OrganizationKey | null } | null> {
        const validated = await patchDomaineValidator.validate(changes)
        const data = await useApiMonOrga<RawDomaine & { currentKey?: OrganizationKey | null }>(
            'PATCH',
            `domaines/${encodeId(id)}`,
            undefined,
            { changes: validated, base },
        )
        if (!data) return null
        return { domaine: mapToDomaine(data), currentKey: data.currentKey ?? null }
    }

    async function reorderDomaines(dirLib: string, domLibs: string[]): Promise<void> {
        await useApiMonOrga('PUT', 'domaines/order', undefined, { dirLib, domLibs })
    }

    async function deleteDomaine(id: string): Promise<boolean> {
        try {
            await useApiMonOrga<unknown>('DELETE', `domaines/${encodeId(id)}`)
            return true
        } catch (error) {
            console.error(`Failed to delete domaine with ID ${id}`, error)
            return false
        }
    }

    return {
        storeDomaine,
        fetchDomaineById,
        updateDomaine,
        patchDomaine,
        mergeDomaine,
        deleteDomaine,
        reorderDomaines,
    }
}
